import React, { PureComponent } from 'react';
import {
  ORG_SELECTOR_ADD_ORGS_CLICK,
  ORG_SELECTOR_CLICK,
  ORG_SELECTOR_SELECTION_CLICK,
  logEvent,
} from '../../utils/analytics';
import Selector, { Item, Organization } from '../Selector';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { iconForProviderId } from '../../utils/providerHelpers';
import { getColor } from '../../utils/colorCycle';
import { Theme } from '@mui/material/styles';
import withTheme from '@mui/styles/withTheme';
import { replaceOrganizationUrl, getSectionUrl } from '../../utils/urlHelpers';

interface GetNewOrgUrlInput {
  newOrg: Organization;
  organization: Organization;
  location: {
    pathname: string;
  };
}

export function getNewOrgUrl({
  newOrg,
  organization,
  location,
}: GetNewOrgUrlInput): string {
  let url = location.pathname;

  // No current org or project set when in user settings, link to newOrg dash
  if (location.pathname.startsWith(`/dash/user/settings/`)) {
    return `/dash/${newOrg.providerId}/${newOrg.slug}/`;
  }

  if (organization) {
    if (
      // if new org is the same as old org
      organization.uuid === newOrg.uuid
    ) {
      return `/dash/${newOrg.providerId}/${newOrg.slug}/`;
    } else {
      // otherwise just go to the base page of whatever section the user is in
      url = replaceOrganizationUrl(location.pathname, organization, newOrg);
      // if they're in organization settings, don't drop to base of the section
      if (!url.startsWith('/dash/settings/')) {
        url = getSectionUrl(url, newOrg);
      }
    }
  }

  return url;
}

interface Props {
  organization: Organization;
  organizations: Organization[];
  location: {
    pathname: string;
  };
}

export class OrganizationSelector extends PureComponent<Props> {
  addOrganizationLine = {
    title: 'Add organizations',
    url: '/dash/user/settings/integrations',
    icon: faPlus,
    iconAlign: 'start',
    onClick: () => {
      logEvent(ORG_SELECTOR_ADD_ORGS_CLICK);
    },
  };

  getItems = (): Item[] => {
    const { organizations, organization, location } = this.props;
    if (!organizations) {
      return [];
    }

    const items: Item[] = organizations.map((org, index) => ({
      title: org.name,
      url: getNewOrgUrl({
        newOrg: org,
        organization,
        location,
      }),
      avatar: true,
      imageUrl: org.photo_url,
      avatarTextBackground: getColor(index),
      icon: iconForProviderId(org.providerId),
      iconAlign: 'end',
      active: organization && org.uuid === organization.uuid,
      onClick: () => this.onClickOrg(org),
    }));

    return items;
  };

  onClickOrg = (newOrg: Organization) => {
    logEvent(ORG_SELECTOR_SELECTION_CLICK);
    localStorage.setItem('last_selected_org_uuid', newOrg.uuid);
  };

  render(): JSX.Element {
    const items = this.getItems();

    return (
      <Selector
        items={items}
        title="Organizations"
        id="organizationSelector"
        width={400}
        onClick={() => logEvent(ORG_SELECTOR_CLICK)}
        footer={this.addOrganizationLine}
      />
    );
  }
}

export default OrganizationSelector;
